// FLOW-01, revisão de UX (M7, M1) — as regras de um parâmetro do flow (`$defs.param` do
// `flow.schema.json`: `enum`, `pattern`, `minLength`/`maxLength`, `minimum`/`maximum`), para quem
// não escreve expressão regular: os atalhos de formato que a caixa do gatilho oferece, a regra em
// leitura clara ("11 dígitos", "entre 1 e 10") e a conferência local de um valor dos dados do teste.
//
// FLOW-12 (Task 4.3, G7, G8): o formulário do teste confere cada campo com os MESMOS textos do
// servidor (`conferirValor`, o `mensagem_do_schema` de `powertools/core/executor.py`), o obrigatório
// vazio com o texto da SPEC (`mensagemDoObrigatorio`), e o primeiro campo errado dos dados inteiros
// (`conferirParams`). A paridade é o contrato `powertools/contract/flow_params_casos.json`, que o
// pytest roda no servidor e o vitest aqui.

// Os formatos prontos, na ordem do seletor. O `pattern` é o que vai ao YAML; um `pattern` que não
// é nenhum destes é "Personalizado", com o texto dele.
export const ATALHOS = [
  { chave: 'digitos', pattern: '^\\d+$' },
  { chave: 'cpf', pattern: '^\\d{11}$' },
  { chave: 'cep', pattern: '^\\d{8}$' },
  { chave: 'email', pattern: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$' },
];

export const atalhoDoPattern = pattern =>
  ATALHOS.find(a => a.pattern === pattern)?.chave || null;

// Quem aceita cada regra (o schema não amarra a regra ao tipo; a caixa só oferece a que faz
// sentido). "Só estes valores" de uma lista vale para cada item (`items.enum`): um `enum` no
// próprio parâmetro lista compararia a lista inteira com cada valor, e nada passaria.
export const aceitaFormato = tipo => tipo === 'string';
export const aceitaFaixa = tipo => tipo === 'integer' || tipo === 'number';

// As chaves que o tipo não aceita (revisão por delta do UX2, P5): a caixa não as mostra nele, e
// deixadas no YAML ao trocar o tipo ficariam invisíveis até o compilador recusar. O `enum` sai do
// sim/não, que não tem o que restringir, e da lista, que guarda os valores no `items.enum`; o
// `items` só vale na lista.
export const chavesQueNaoAceita = tipo => [
  ...(aceitaFormato(tipo) ? [] : ['pattern', 'minLength', 'maxLength']),
  ...(aceitaFaixa(tipo) ? [] : ['minimum', 'maximum']),
  ...(tipo === 'boolean' || tipo === 'array' ? ['enum'] : []),
  ...(tipo === 'array' ? [] : ['items']),
];

// O tipo dos valores de "Só estes valores": o do parâmetro, ou o dos itens de uma lista.
export const tipoDosValores = def =>
  def?.type === 'array' ? def.items?.type || 'string' : def?.type;

// O texto digitado no tipo do valor; `undefined` quando não é desse tipo (o "abc" de um inteiro).
export function converter(tipo, texto) {
  const limpo = String(texto).trim();
  if (tipo === 'integer') {
    return /^-?\d+$/.test(limpo) ? Number(limpo) : undefined;
  }
  if (tipo === 'number') {
    return limpo !== '' && Number.isFinite(Number(limpo))
      ? Number(limpo)
      : undefined;
  }
  if (tipo === 'boolean') {
    if (limpo === 'true') return true;
    return limpo === 'false' ? false : undefined;
  }
  return limpo === '' ? undefined : String(texto);
}

const N_DIGITOS = /^\^\\d\{(\d+)\}\$$/;

// As partes da regra em leitura clara, como `{chave, valores}` para o i18n de quem mostra
// (`STEP_BOX.TRIGGER.RULE.SUMMARY.<chave>`). Vazia, o parâmetro não tem regra.
export function resumoDaRegra(def) {
  if (!def) return [];
  const partes = [];
  const { pattern } = def;
  if (typeof pattern === 'string' && pattern) {
    const digitos = pattern.match(N_DIGITOS);
    if (digitos) partes.push({ chave: 'N_DIGITS', valores: { n: digitos[1] } });
    else if (atalhoDoPattern(pattern) === 'digitos') {
      partes.push({ chave: 'DIGITS', valores: {} });
    } else if (atalhoDoPattern(pattern) === 'email') {
      partes.push({ chave: 'EMAIL', valores: {} });
    } else partes.push({ chave: 'CUSTOM', valores: { pattern } });
  }
  const faixa = (min, max, prefixo) => {
    const temMin = Number.isFinite(min);
    const temMax = Number.isFinite(max);
    if (temMin && temMax) {
      partes.push({ chave: `${prefixo}BETWEEN`, valores: { min, max } });
    } else if (temMin)
      partes.push({ chave: `${prefixo}MIN`, valores: { min } });
    else if (temMax) partes.push({ chave: `${prefixo}MAX`, valores: { max } });
  };
  faixa(def.minLength, def.maxLength, 'LENGTH_');
  faixa(def.minimum, def.maximum, '');
  const valores = def.type === 'array' ? def.items?.enum : def.enum;
  if (Array.isArray(valores) && valores.length) {
    partes.push({ chave: 'ONLY', valores: { valores: valores.join(', ') } });
  }
  return partes;
}

// O que um valor dos dados do teste fere nas regras do parâmetro, como `{chave, valores}` para o
// i18n (`TEST.FORM.RULES.<chave>`); nulo quando passa. Só confere o valor do tipo certo: o tipo
// errado é outro aviso, de quem monta o campo. FLOW-12: é o aviso do formulário de antes, no
// popover do painel, até a gaveta da Task 5.1 trocá-lo pelo `conferirValor`.
export function avisoDaRegra(def, valor) {
  if (!def || valor === undefined || valor === null || valor === '') {
    return null;
  }
  if (Array.isArray(def.enum) && !def.enum.includes(valor)) {
    return { chave: 'ENUM', valores: { valores: def.enum.join(', ') } };
  }
  if (typeof valor === 'string') {
    if (typeof def.pattern === 'string' && def.pattern) {
      let casa = true;
      try {
        // `u`: o `\d` e o `\s` do Python e do JavaScript concordam no que os atalhos usam.
        casa = new RegExp(def.pattern, 'u').test(valor);
      } catch {
        casa = true; // regex que o JavaScript não lê: o servidor confere
      }
      if (!casa) return { chave: 'PATTERN', valores: {} };
    }
    if (Number.isFinite(def.minLength) && valor.length < def.minLength) {
      return { chave: 'MIN_LENGTH', valores: { min: def.minLength } };
    }
    if (Number.isFinite(def.maxLength) && valor.length > def.maxLength) {
      return { chave: 'MAX_LENGTH', valores: { max: def.maxLength } };
    }
  }
  if (typeof valor === 'number') {
    if (Number.isFinite(def.minimum) && valor < def.minimum) {
      return { chave: 'MINIMUM', valores: { min: def.minimum } };
    }
    if (Number.isFinite(def.maximum) && valor > def.maximum) {
      return { chave: 'MAXIMUM', valores: { max: def.maximum } };
    }
  }
  if (Array.isArray(valor) && def.type === 'array') {
    const ruim = valor.find(item => avisoDaRegra(def.items, item));
    if (ruim !== undefined) return avisoDaRegra(def.items, ruim);
  }
  return null;
}

// -- FLOW-12 (G7): os textos do servidor

const ehObjeto = valor =>
  !!valor && typeof valor === 'object' && !Array.isArray(valor);
const tem = (objeto, chave) =>
  Object.prototype.hasOwnProperty.call(objeto, chave);

// O nome de cada tipo e o do valor que veio, como o `_TIPO_PT` e o `_tipo_do_valor` do servidor.
const TIPO_PT = {
  string: 'um texto',
  integer: 'um número inteiro',
  number: 'um número',
  boolean: 'verdadeiro ou falso',
  array: 'uma lista',
  object: 'um objeto',
  null: 'null',
};
function tipoDoValor(valor) {
  if (valor === null) return 'null';
  if (typeof valor === 'boolean') return 'verdadeiro ou falso';
  if (typeof valor === 'number') {
    return Number.isInteger(valor) ? 'um número inteiro' : 'um número';
  }
  if (typeof valor === 'string') return 'um texto';
  if (Array.isArray(valor)) return 'uma lista';
  return ehObjeto(valor) ? 'um objeto' : typeof valor;
}

// Os tetos do servidor (`TETO_DO_NOME`, `TETO_DO_TEXTO`), contados em caracteres, como o `len` do
// Python, e com o "…" dentro.
const TETO_DO_NOME = 80;
const TETO_DO_TEXTO = 1000;
function cortado(texto, teto) {
  const caracteres = [...texto];
  return caracteres.length <= teto
    ? texto
    : `${caracteres.slice(0, teto - 1).join('')}…`;
}

// `endereco.cep`, `itens[0].sku`: o caminho do valor dentro dos params, como se lê no flow.
function caminhoDoArg(partes) {
  const texto = partes.reduce((acc, parte) => {
    if (typeof parte === 'number') return `${acc}[${parte}]`;
    const segmento = cortado(String(parte), TETO_DO_NOME);
    return acc ? `${acc}.${segmento}` : segmento;
  }, '');
  return cortado(texto, TETO_DO_NOME);
}

const numeroPt = n =>
  Number.isInteger(n) ? String(n) : String(n).replace('.', ',');

const emListaPt = (itens, conjuncao = 'e') =>
  itens.length === 1
    ? itens[0]
    : `${itens.slice(0, -1).join(', ')} ${conjuncao} ${itens[itens.length - 1]}`;

// O `json.dumps` do Python, com os separadores dele (`, ` e `: `): é como o servidor escreve cada
// valor do `enum` na mensagem.
function jsonPy(valor) {
  if (Array.isArray(valor)) return `[${valor.map(jsonPy).join(', ')}]`;
  if (ehObjeto(valor)) {
    const pares = Object.entries(valor).map(
      ([chave, v]) => `${JSON.stringify(chave)}: ${jsonPy(v)}`
    );
    return `{${pares.join(', ')}}`;
  }
  return JSON.stringify(valor);
}

// O tipo do JSON Schema (o do `jsonschema` do servidor): o booleano não é número, e o inteiro é
// número.
const DO_TIPO = {
  string: v => typeof v === 'string',
  integer: v => typeof v === 'number' && Number.isInteger(v),
  number: v => typeof v === 'number',
  boolean: v => typeof v === 'boolean',
  array: v => Array.isArray(v),
  object: ehObjeto,
  null: v => v === null,
};
const casaTipo = (tipos, valor) =>
  [].concat(tipos).some(tipo => DO_TIPO[tipo]?.(valor) ?? false);

const igual = (a, b) => {
  if (typeof a !== typeof b || Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  return typeof a === 'object' && a !== null
    ? JSON.stringify(a) === JSON.stringify(b)
    : a === b;
};

// O `pattern` no `RegExp` com `u`: o `\d` e o `\s` do RE2 e do JavaScript concordam no que os
// atalhos usam. O padrão que o JavaScript não compila (`(?i)`, `(?P<nome>…)`, que o RE2 aceita) não
// é conferido no campo: o servidor decide (G7).
function casaPadrao(padrao, texto) {
  let regex;
  try {
    regex = new RegExp(padrao, 'u');
  } catch {
    return true;
  }
  return regex.test(texto);
}

// O schema de um param como o compilador o põe no `args_schema` (`_esquema_do_param`): o `required`
// booleano e o `example` saem; o `object` com `properties` não aceita outra chave, e a lista
// `required` dele vai como está.
function esquemaDoParam(def) {
  const { required, properties, example, ...resto } = def;
  if (def.type !== 'object')
    return { ...resto, ...(properties ? { properties } : {}) };
  const saida = { ...resto };
  if (tem(def, 'properties')) {
    saida.properties = Object.fromEntries(
      Object.entries(properties || {}).map(([nome, sub]) => [
        nome,
        ehObjeto(sub) ? esquemaDoParam(sub) : sub,
      ])
    );
    saida.additionalProperties = false;
  }
  if (Array.isArray(required) && required.length)
    saida.required = [...required];
  return saida;
}

// As falhas de um valor contra o schema, na ordem em que o `jsonschema` do servidor as acha: as
// regras em ordem alfabética (o servidor monta o validador do JSON com as chaves em ordem), e cada
// propriedade e cada item descendo com o caminho.
function falhasDe(schema, valor, caminho) {
  const falhas = [];
  const falha = regra =>
    falhas.push({ regra, limite: schema[regra], valor, schema, caminho });
  Object.keys(schema)
    .sort()
    .forEach(regra => {
      const limite = schema[regra];
      if (
        regra === 'additionalProperties' &&
        limite === false &&
        ehObjeto(valor)
      ) {
        const aceitos = Object.keys(schema.properties || {});
        if (Object.keys(valor).some(k => !aceitos.includes(k))) falha(regra);
      } else if (regra === 'enum' && Array.isArray(limite)) {
        if (!limite.some(opcao => igual(opcao, valor))) falha(regra);
      } else if (
        regra === 'items' &&
        ehObjeto(limite) &&
        Array.isArray(valor)
      ) {
        valor.forEach((item, i) =>
          falhas.push(...falhasDe(limite, item, [...caminho, i]))
        );
      } else if (regra === 'maxLength' && typeof valor === 'string') {
        if ([...valor].length > limite) falha(regra);
      } else if (regra === 'minLength' && typeof valor === 'string') {
        if ([...valor].length < limite) falha(regra);
      } else if (regra === 'maximum' && DO_TIPO.number(valor)) {
        if (valor > limite) falha(regra);
      } else if (regra === 'minimum' && DO_TIPO.number(valor)) {
        if (valor < limite) falha(regra);
      } else if (regra === 'pattern' && typeof valor === 'string') {
        if (!casaPadrao(limite, valor)) falha(regra);
      } else if (
        regra === 'properties' &&
        ehObjeto(limite) &&
        ehObjeto(valor)
      ) {
        Object.keys(limite)
          .sort()
          .filter(nome => tem(valor, nome) && ehObjeto(limite[nome]))
          .forEach(nome =>
            falhas.push(
              ...falhasDe(limite[nome], valor[nome], [...caminho, nome])
            )
          );
      } else if (
        regra === 'required' &&
        Array.isArray(limite) &&
        ehObjeto(valor)
      ) {
        limite.filter(nome => !tem(valor, nome)).forEach(() => falha(regra));
      } else if (regra === 'type' && !casaTipo(limite, valor)) {
        falha(regra);
      }
    });
  return falhas;
}

// A falha que o servidor mostra (o `best_match` do `jsonschema` 4.26): a do caminho mais curto; no
// empate, a do caminho maior na comparação item a item (o item 1 antes do 0, `numero` antes de
// `cep`); no empate de novo, a do valor que nem é do tipo do schema dela; e, por fim, a primeira.
function compararCaminhos(a, b) {
  const k = a.findIndex((parte, i) => i < b.length && parte !== b[i]);
  if (k >= 0) return a[k] > b[k] ? 1 : -1;
  return a.length - b.length;
}
const foraDoTipo = f =>
  f.schema.type === undefined || !casaTipo(f.schema.type, f.valor);
function maisRelevante(f, melhor) {
  if (f.caminho.length !== melhor.caminho.length) {
    return f.caminho.length < melhor.caminho.length;
  }
  const caminhos = compararCaminhos(f.caminho, melhor.caminho);
  if (caminhos !== 0) return caminhos > 0;
  return foraDoTipo(f) && !foraDoTipo(melhor);
}
const principal = falhas =>
  falhas.reduce((melhor, f) => (maisRelevante(f, melhor) ? f : melhor));

// A mensagem da falha, como o `mensagem_do_schema` a escreve, e o caminho do campo a corrigir.
function mensagemDa({ regra, limite, valor, schema, caminho }) {
  const texto = caminhoDoArg(caminho);
  const campo = texto ? `\`${texto}\`` : 'o valor';
  if (regra === 'required') {
    const faltam = limite.filter(p => ehObjeto(valor) && !tem(valor, p));
    const nomes = (faltam.length ? faltam : limite).map(
      p => `\`${caminhoDoArg([...caminho, p])}\``
    );
    return {
      mensagem:
        nomes.length === 1
          ? `falta o campo ${nomes[0]}.`
          : `faltam os campos ${emListaPt(nomes)}.`,
      foco: [...caminho, faltam[0] ?? limite[0]],
    };
  }
  if (regra === 'additionalProperties') {
    const aceitos = Object.keys(schema.properties || {});
    const extras = Object.keys(valor).filter(k => !aceitos.includes(k));
    const nomes = extras.map(k => `\`${caminhoDoArg([...caminho, k])}\``);
    return {
      mensagem:
        nomes.length === 1
          ? `o campo ${nomes[0]} não existe nos parâmetros.`
          : `os campos ${emListaPt(nomes)} não existem nos parâmetros.`,
      foco: [...caminho, extras[0]],
    };
  }
  let mensagem = `${campo} não passou na regra \`${regra}\` dos parâmetros.`;
  if (regra === 'type') {
    const esperado = emListaPt(
      [].concat(limite).map(t => TIPO_PT[t] ?? String(t)),
      'ou'
    );
    mensagem = texto
      ? `${campo} precisa ser ${esperado}; veio ${tipoDoValor(valor)}.`
      : `os parâmetros precisam ser ${esperado}; vieram ${tipoDoValor(valor)}.`;
  } else if (regra === 'minLength' || regra === 'maxLength') {
    const unidade = limite === 1 ? 'caractere' : 'caracteres';
    mensagem =
      regra === 'minLength'
        ? `${campo} precisa ter pelo menos ${limite} ${unidade}.`
        : `${campo} pode ter no máximo ${limite} ${unidade}.`;
  } else if (regra === 'minimum') {
    mensagem = `${campo} precisa ser no mínimo ${numeroPt(limite)}.`;
  } else if (regra === 'maximum') {
    mensagem = `${campo} pode ser no máximo ${numeroPt(limite)}.`;
  } else if (regra === 'enum') {
    const opcoes = limite.map(jsonPy);
    const quais = opcoes.length > 1 ? 'um destes valores' : 'o valor';
    mensagem = `${campo} precisa ser ${quais}: ${emListaPt(opcoes, 'ou')}.`;
  } else if (regra === 'pattern') {
    mensagem = `${campo} não segue o formato pedido (o padrão \`${limite}\`).`;
  }
  return { mensagem, foco: caminho };
}

// O que um valor de um param fere, com o texto do servidor: `{mensagem, caminho}`, em que `caminho`
// é a lista das partes do campo a corrigir (`['end', 'cep']`, `['ids', 1]`), ou nulo quando passa.
// `nome` é o do param, que a mensagem cita. Confere o `type` (com o `object`, as `properties` e o
// `required` dele), o `enum`, o `pattern`, os tamanhos, a faixa e os itens. O vazio de um param
// obrigatório é do `conferirParams`, com o texto da SPEC.
export function conferirValor(def, valor, nome = '') {
  if (!ehObjeto(def) || valor === undefined) return null;
  const falhas = falhasDe(
    esquemaDoParam(def),
    valor,
    nome === '' ? [] : [nome]
  );
  if (!falhas.length) return null;
  const { mensagem, foco } = mensagemDa(principal(falhas));
  return { mensagem: cortado(mensagem, TETO_DO_TEXTO), caminho: foco };
}

// O obrigatório vazio, com o texto da SPEC (T1), e não o "falta o campo" do servidor (G7).
export const mensagemDoObrigatorio = nome =>
  `Preencha \`${nome}\`: o parâmetro é obrigatório.`;

// Vazio, para o obrigatório: o campo sem valor, o `null` e o texto vazio. A lista vazia e o objeto
// vazio são valores (o objeto vazio ainda falha pelo `required` dele).
const vazio = valor => valor === undefined || valor === null || valor === '';

// O param obrigatório: o `object` sempre, como no compilador; o resto, sem `required: false`.
export const ehObrigatorio = def =>
  ehObjeto(def) && (def.type === 'object' || def.required !== false);

// FLOW-12 (Task 5.1, T1): o param opcional vazio (o texto apagado, o `null`) é o param que o agente
// não mandaria. Ele sai dos dados antes de conferir e antes de ir ao teste: o texto vazio de um
// param opcional com `pattern` não passaria no servidor, e o campo que a pessoa deixou em branco não
// é um valor que ela escolheu. O `object` não sai: ele é sempre obrigatório.
export function semOpcionaisVazios(definicoes, valores) {
  const defs = ehObjeto(definicoes) ? definicoes : {};
  return Object.fromEntries(
    Object.entries(ehObjeto(valores) ? valores : {}).filter(
      ([nome, valor]) =>
        !(
          ehObjeto(defs[nome]) &&
          !ehObrigatorio(defs[nome]) &&
          (valor === '' || valor === null)
        )
    )
  );
}

// O erro de um param, com o texto da SPEC para o obrigatório vazio e o do servidor para o resto:
// `{campo, caminho, mensagem}`, ou nulo. É o que o formulário mostra em cada campo (T1).
export function erroDoParam(def, valor, nome) {
  if (ehObrigatorio(def) && vazio(valor)) {
    return {
      campo: nome,
      caminho: [nome],
      mensagem: mensagemDoObrigatorio(nome),
    };
  }
  const erro = conferirValor(def, valor, nome);
  return erro ? { campo: nome, ...erro } : null;
}

// O primeiro campo errado dos dados do teste, na ordem do `params` do YAML: `{campo, caminho,
// mensagem}`, ou nulo quando todos passam. O `object` é sempre obrigatório, como no compilador; o
// param `required: false` vazio fica de fora (`semOpcionaisVazios`). Por último, a chave que não é
// param, que o servidor recusa.
export function conferirParams(definicoes, valores) {
  const defs = ehObjeto(definicoes) ? definicoes : {};
  const dados = semOpcionaisVazios(defs, valores);
  let achado = null;
  Object.entries(defs).some(([nome, def]) => {
    const valor = tem(dados, nome) ? dados[nome] : undefined;
    achado = erroDoParam(def, valor, nome);
    return !!achado;
  });
  if (achado) return achado;
  const extras = Object.keys(dados).filter(nome => !tem(defs, nome));
  if (!extras.length) return null;
  const nomes = extras.map(nome => `\`${caminhoDoArg([nome])}\``);
  return {
    campo: extras[0],
    caminho: [extras[0]],
    mensagem:
      nomes.length === 1
        ? `o campo ${nomes[0]} não existe nos parâmetros.`
        : `os campos ${emListaPt(nomes)} não existem nos parâmetros.`,
  };
}

// O valor com que o campo abre (T1, R7): o que a pessoa usou por último (`salvo`), senão o
// `example` do param, senão vazio (`undefined`).
export function valorInicial(param, salvo) {
  if (salvo !== undefined) return salvo;
  return ehObjeto(param) && tem(param, 'example') ? param.example : undefined;
}
